import { QuoteProductCard, QuoteQuantitySelector } from '~/purchase/components';
import { isNil, pick } from 'lodash-es';
import { Button, Flex, IconButton } from '@wyz/ui';
import { Trans } from '~/common/context';
import { useFormContext, useWatch } from 'react-hook-form';
import { useManageQuoteCart } from '~/purchase/api';
import { useQuoteCart } from '~/purchase/hooks';
import {
  IQuoteAddToCartForm,
  IQuoteItemInfo,
  IQuoteTyreOffer,
} from '~/purchase/types';
import { IUserCustomer } from '~/common/types';
import { invariant } from '@wyz/utils';

type Props = {
  quoteItemInfo: IQuoteItemInfo;
  customer: IUserCustomer;
  showBfDiscount: boolean;
  pointOfSaleView: boolean;
  tyreOffer: IQuoteTyreOffer;
  onRefreshTyre: (
    ean: string,
    quantity: number,
    forceNoLatency?: boolean,
  ) => void;
  isRefreshingTyre: boolean;
  onShowDetails: () => void;
  addItemLoading: boolean;
};

function QuoteStandardItem({
  quoteItemInfo,
  customer,
  showBfDiscount,
  pointOfSaleView,
  tyreOffer,
  onRefreshTyre,
  isRefreshingTyre,
  onShowDetails,
  addItemLoading,
}: Props) {
  const methods = useFormContext<IQuoteAddToCartForm>();

  const watchedQuantity = useWatch({
    name: 'quantity',
    control: methods.control,
  });

  const { actions } = useQuoteCart();
  const manageCart = useManageQuoteCart({
    get: { enabled: true },
  });

  const cartItem = manageCart.get.data?.data.items.find(
    (cartItem) => cartItem.tyre.ean === quoteItemInfo.tyre.ean,
  );
  return (
    <QuoteProductCard
      item={quoteItemInfo}
      onShowDetails={onShowDetails}
      buyingInfo={
        quoteItemInfo.pricing
          ? pick(quoteItemInfo.pricing, ['buyingPrice', 'margin'])
          : undefined
      }
      pointOfSaleView={pointOfSaleView}
      requestedQuantity={watchedQuantity}
      customer={customer}
      showBfDiscount={showBfDiscount}
      currency={tyreOffer.tyre.priceFrame.currencyIsoCode}
    >
      {quoteItemInfo.pricing && (
        <div className='my-2'>
          <QuoteQuantitySelector
            name='quantity'
            price={
              quoteItemInfo.containsFallbackOffers
                ? null
                : quoteItemInfo.pricing.unitPriceTaxIncl
            }
            currency={tyreOffer.tyre.priceFrame.currencyIsoCode}
            onChange={(quantity) => onRefreshTyre(tyreOffer.tyre.ean, quantity)}
          />
        </div>
      )}
      {(quoteItemInfo.containsGspFallbackOffers ||
        quoteItemInfo.containsFallbackOffers) && (
        <Button
          className='mb-1'
          id={`refresh-gsp-offers-${tyreOffer.tyre.ean}`}
          variant='primary'
          fullWidth
          isLoading={isRefreshingTyre}
          onClick={() =>
            onRefreshTyre(tyreOffer.tyre.ean, watchedQuantity, true)
          }
        >
          <Trans i18nKey='app.common.buttons.search' />
        </Button>
      )}
      <Flex className='gap-1'>
        {quoteItemInfo.pricing && (
          <Button
            id={`add-to-quote-${quoteItemInfo.tyre.ean}`}
            type='submit'
            variant='primary'
            fullWidth
            isLoading={addItemLoading}
            disabled={
              !isNil(cartItem) ||
              quoteItemInfo.containsGspFallbackOffers ||
              quoteItemInfo.containsFallbackOffers
            }
          >
            <Trans i18nKey='app.purchase.quote_search_page.add_to_quote' />
          </Button>
        )}
        {!isNil(cartItem) && (
          <IconButton
            id={`remove-from-cart-${quoteItemInfo.tyre.ean}`}
            icon='trash'
            iconSize='sm'
            iconAlign='right'
            variant='tertiary-grey'
            onClick={() => {
              invariant(quoteItemInfo.pricing);
              const payload = {
                ...pick(quoteItemInfo, ['tyre', 'stocks']),
                buyingPrice: quoteItemInfo.pricing.buyingPrice,
                unitPriceTaxIncl: quoteItemInfo.pricing.unitPriceTaxIncl,
                recyclingFee: quoteItemInfo.pricing.recyclingFee,
                quantity: watchedQuantity,
                currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
              };
              actions.removeItem.process(cartItem.id, payload);
            }}
            isLoading={actions.removeItem.isLoading}
          >
            <Trans i18nKey='app.purchase.quote_search_page.product.delete' />
          </IconButton>
        )}
      </Flex>
    </QuoteProductCard>
  );
}

export default QuoteStandardItem;
